/**
 * O botão do miolo de Finanças, nas sete cores e nos três tamanhos da origem.
 *
 * Papel: reproduz `.btn` e as variantes `.btn-primary`, `.btn-gold`,
 * `.btn-ghost`, `.btn-danger` e `.btn-success` (l. 36–42), mais as duas cores
 * que a barra de lote escreve por `style` — "Marcar como Lançado" e "Marcar
 * como Provisionado" (l. 349–350): `6px 13px`, 12 px 600, raio de 6 px.
 *
 * Como usar: `variante` escolhe a cor, `tamanho="sm"` dá o `.btn-sm` e `icone`
 * dá o `.btn-icon` dos botões de um símbolo só (✎, ✓, 🗑, ⚙, ✕). Com os dois,
 * vale o respiro do `icone` e a fonte do `sm`, como na origem, onde
 * `.btn-icon` vem depois de `.btn-sm`. O botão nasce com `type="button"`:
 * dentro de um `<form>`, um `submit` implícito faria Enter num campo gravar, e a
 * origem só grava no clique.
 *
 * Desabilitado, o botão não muda de cor: quem o desabilita escolhe a opacidade,
 * como o ✕ do favorecido em uso, que fica a 35 %.
 */
import { type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/** As sete cores de botão da origem. */
export type VarianteDoBotao =
  'primario' | 'ouro' | 'fantasma' | 'perigo' | 'sucesso' | 'lancado' | 'provisionado';

/** O fundo, o texto, a borda e o hover de cada variante. */
const CLASSES_DA_VARIANTE: Readonly<Record<VarianteDoBotao, string>> = {
  primario: 'bg-foreground text-superficie hover:bg-emb-grafite',
  ouro: 'border border-brass-light bg-brass text-superficie hover:bg-brass-light',
  fantasma: 'border border-emb-borda bg-background text-foreground hover:bg-emb-hover-bege',
  perigo:
    'border border-emb-perigo-borda bg-emb-perigo-fundo text-perigo hover:bg-emb-perigo-hover',
  sucesso:
    'border border-emb-sucesso-borda bg-emb-sucesso-fundo text-sucesso hover:bg-emb-sucesso-hover',
  lancado: 'border border-emb-lancado-borda bg-emb-lancado-fundo text-emb-lancado-texto',
  provisionado: 'border border-emb-amarelo/35 bg-emb-amarelo/15 text-emb-amarelo',
};

interface BotaoProps extends ComponentProps<'button'> {
  /** A cor; `primario` quando ausente, que é o `.btn-primary`. */
  variante?: VarianteDoBotao;
  /** `sm` é o `.btn-sm`: `4px 9px` e 11 px. */
  tamanho?: 'normal' | 'sm';
  /** O `.btn-icon`: `5px 7px`, para um símbolo só. */
  icone?: boolean;
}

/**
 * Um botão do miolo de Finanças.
 *
 * @param variante - A cor da origem; `primario` quando ausente.
 * @param tamanho - `sm` para o botão pequeno das linhas e das barras.
 * @param icone - Verdadeiro para o botão de um símbolo só.
 * @param className - Classes de quem chama, aplicadas por último pelo `cn()`.
 * @param type - O `type` do botão; `button` quando ausente.
 * @returns O botão.
 */
export function Botao({
  variante = 'primario',
  tamanho = 'normal',
  icone = false,
  className,
  type = 'button',
  ...props
}: BotaoProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex cursor-pointer items-center gap-[5px] rounded-[6px] border-0 px-[13px] py-1.5 font-[inherit] text-[12px] font-semibold whitespace-nowrap transition-all duration-150',
        tamanho === 'sm' && 'px-[9px] py-1 text-[11px]',
        icone && 'px-[7px] py-[5px]',
        CLASSES_DA_VARIANTE[variante],
        className,
      )}
      {...props}
    />
  );
}
